/**
 * Admin Pricing Page
 * Self-service pricing management - edit plans and sync to Stripe
 */

import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { Button, useToast } from '@powercore/ui';

interface PlanFeature {
  id?: string;
  plan_id?: string;
  feature_id: string;
  feature_name: string;
  limit_value: number;
}

interface Plan {
  id: string;
  name: string;
  description: string;
  price_cents: number;
  currency: string;
  billing_interval: string;
  stripe_price_id: string | null;
  stripe_product_id: string | null;
  is_active: boolean;
  sort_order: number;
  features: PlanFeature[];
}

interface EditingPlan extends Plan {
  isEditing?: boolean;
}

export function AdminPricingPage() {
  const [plans, setPlans] = useState<EditingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [syncing, setSyncing] = useState<string | null>(null);
  const { addToast } = useToast();

  useEffect(() => {
    loadPlans();
  }, []);

  async function loadPlans() {
    setLoading(true);
    const result = await api.getAdminPlans();

    // Handle double-wrapped response: result.data.data is the actual array
    const plansData = (result.data as any)?.data || result.data;

    if (plansData && Array.isArray(plansData)) {
      setPlans(plansData);
    } else {
      addToast({
        title: 'Failed to load plans',
        description: result.error?.message || 'Invalid response format',
        variant: 'error',
      });
    }
    setLoading(false);
  }

  function startEditing(planId: string) {
    setPlans((prev) =>
      prev.map((p) => (p.id === planId ? { ...p, isEditing: true } : p))
    );
  }

  function cancelEditing() {
    loadPlans(); // Reset to server state
  }

  function updatePlanField(planId: string, field: keyof Plan, value: unknown) {
    setPlans((prev) =>
      prev.map((p) =>
        p.id === planId ? { ...p, [field]: value } : p
      )
    );
  }

  function updateFeatureLimit(planId: string, featureId: string, value: number) {
    setPlans((prev) =>
      prev.map((p) =>
        p.id === planId
          ? {
              ...p,
              features: p.features.map((f) =>
                f.feature_id === featureId ? { ...f, limit_value: value } : f
              ),
            }
          : p
      )
    );
  }

  async function savePlan(plan: EditingPlan) {
    setSaving(plan.id);
    const result = await api.updateAdminPlan(plan.id, {
      name: plan.name,
      description: plan.description,
      price_cents: plan.price_cents,
      currency: plan.currency,
      billing_interval: plan.billing_interval,
      is_active: plan.is_active,
      features: plan.features.map((f) => ({
        feature_id: f.feature_id,
        feature_name: f.feature_name,
        limit_value: f.limit_value,
      })),
    });

    setSaving(null);

    // Check if the actual data exists (handle double-wrapped response)
    const updatedPlan = (result.data as any)?.data || result.data;

    if (updatedPlan && !result.error) {
      addToast({
        title: 'Plan updated',
        description: `${plan.name} has been updated`,
        variant: 'success',
      });
      // Reload all plans to get fresh data
      await loadPlans();
    } else {
      addToast({
        title: 'Failed to update plan',
        description: result.error?.message || 'Update failed',
        variant: 'error',
      });
    }
  }

  async function syncToStripe(plan: Plan) {
    if (plan.price_cents === 0) {
      addToast({
        title: 'Free plan',
        description: 'Free plan does not need Stripe sync',
        variant: 'info',
      });
      return;
    }

    setSyncing(plan.id);
    const result = await api.syncPlanToStripe(plan.id);
    setSyncing(null);

    const syncData = (result.data as any)?.data || result.data;

    if (syncData && !result.error) {
      addToast({
        title: 'Synced to Stripe',
        description: `${plan.name} price created in Stripe`,
        variant: 'success',
      });
      await loadPlans();
    } else {
      addToast({
        title: 'Stripe sync failed',
        description: result.error?.message || 'Check Stripe API keys',
        variant: 'error',
      });
    }
  }

  function formatPrice(cents: number, currency: string) {
    const amount = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">Loading plans...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Pricing Management</h1>
        <p className="text-gray-600 mt-2">
          Configure your pricing plans and sync to Stripe
        </p>
      </div>

      <div className="space-y-6">
        {!Array.isArray(plans) && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">
              Error: Plans data is not an array. Check console for details.
            </p>
            <p className="text-sm text-red-600 mt-2">
              Type: {typeof plans}, Value: {JSON.stringify(plans)}
            </p>
          </div>
        )}
        {Array.isArray(plans) && plans.length === 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-gray-600">No plans found. Check if migration was applied.</p>
          </div>
        )}
        {Array.isArray(plans) && plans.map((plan) => (
          <div
            key={plan.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                {plan.isEditing ? (
                  <input
                    type="text"
                    value={plan.name}
                    onChange={(e) =>
                      updatePlanField(plan.id, 'name', e.target.value)
                    }
                    className="text-2xl font-bold border-b border-gray-300 focus:border-mazo-500 outline-none"
                  />
                ) : (
                  <h2 className="text-2xl font-bold text-gray-900">
                    {plan.name}
                  </h2>
                )}

                {plan.isEditing ? (
                  <textarea
                    value={plan.description}
                    onChange={(e) =>
                      updatePlanField(plan.id, 'description', e.target.value)
                    }
                    className="mt-1 w-full text-gray-600 border border-gray-300 rounded px-2 py-1"
                    rows={2}
                  />
                ) : (
                  <p className="text-gray-600 mt-1">{plan.description}</p>
                )}
              </div>

              <div className="flex gap-2 ml-4">
                {plan.isEditing ? (
                  <>
                    <Button
                      onClick={() => savePlan(plan)}
                      disabled={saving === plan.id}
                      variant="primary"
                      size="sm"
                    >
                      {saving === plan.id ? 'Saving...' : 'Save'}
                    </Button>
                    <Button
                      onClick={() => cancelEditing()}
                      variant="secondary"
                      size="sm"
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      onClick={() => startEditing(plan.id)}
                      variant="secondary"
                      size="sm"
                    >
                      Edit
                    </Button>
                    <Button
                      onClick={() => syncToStripe(plan)}
                      disabled={syncing === plan.id || plan.price_cents === 0}
                      variant="primary"
                      size="sm"
                    >
                      {syncing === plan.id ? 'Syncing...' : 'Sync to Stripe'}
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Pricing */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price (cents)
                </label>
                {plan.isEditing ? (
                  <input
                    type="number"
                    value={plan.price_cents}
                    onChange={(e) =>
                      updatePlanField(
                        plan.id,
                        'price_cents',
                        parseInt(e.target.value)
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                ) : (
                  <div className="text-2xl font-bold text-mazo-600">
                    {formatPrice(plan.price_cents, plan.currency)}
                  </div>
                )}
                {!plan.isEditing && (
                  <div className="text-sm text-gray-500">
                    {plan.price_cents} cents
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Currency
                </label>
                {plan.isEditing ? (
                  <select
                    value={plan.currency}
                    onChange={(e) =>
                      updatePlanField(plan.id, 'currency', e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                    <option value="GBP">GBP</option>
                  </select>
                ) : (
                  <div className="text-lg text-gray-900">
                    {plan.currency.toUpperCase()}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Interval
                </label>
                {plan.isEditing ? (
                  <select
                    value={plan.billing_interval}
                    onChange={(e) =>
                      updatePlanField(plan.id, 'billing_interval', e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="month">Monthly</option>
                    <option value="year">Yearly</option>
                  </select>
                ) : (
                  <div className="text-lg text-gray-900">
                    {plan.billing_interval === 'month' ? 'Monthly' : 'Yearly'}
                  </div>
                )}
              </div>
            </div>

            {/* Features */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Feature Limits
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {plan.features.map((feature) => (
                  <div
                    key={feature.feature_id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-md"
                  >
                    <span className="text-sm text-gray-700">
                      {feature.feature_name}
                    </span>
                    {plan.isEditing ? (
                      <input
                        type="number"
                        value={feature.limit_value}
                        onChange={(e) =>
                          updateFeatureLimit(
                            plan.id,
                            feature.feature_id,
                            parseInt(e.target.value)
                          )
                        }
                        className="w-20 px-2 py-1 border border-gray-300 rounded text-right"
                      />
                    ) : (
                      <span className="text-lg font-semibold text-mazo-600">
                        {feature.limit_value}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Stripe Info */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              {plan.stripe_price_id ? (
                <div className="flex items-center gap-4 text-sm flex-wrap">
                  <span className="text-gray-500">Stripe Price ID:</span>
                  <code className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">
                    {plan.stripe_price_id}
                  </code>
                  <span className="text-gray-500">Product ID:</span>
                  <code className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">
                    {plan.stripe_product_id || 'Not set'}
                  </code>
                  {plan.stripe_product_id && plan.stripe_product_id !== `mazo_${plan.id}` && (
                    <span className="text-xs text-amber-600 font-medium">
                      (expected: mazo_{plan.id} -- re-sync to fix)
                    </span>
                  )}
                </div>
              ) : plan.price_cents > 0 ? (
                <div className="flex items-center gap-2 text-amber-600">
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-sm font-medium">
                    Not synced to Stripe yet - click "Sync to Stripe"
                  </span>
                  {plan.stripe_product_id && (
                    <span className="text-xs text-gray-500 ml-2">
                      (Product ID: {plan.stripe_product_id})
                    </span>
                  )}
                </div>
              ) : (
                <div className="text-sm text-gray-400">
                  Free plan - no Stripe sync needed
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Help Text */}
      <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">
          💡 How it works
        </h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>
            • <strong>Edit</strong>: Update plan prices and feature limits
          </li>
          <li>
            • <strong>Sync to Stripe</strong>: Creates a new Stripe Price (prices
            are immutable in Stripe)
          </li>
          <li>
            • Old prices are automatically archived and tracked in price history
          </li>
          <li>
            • Free plan (€0) doesn't need Stripe sync
          </li>
        </ul>
      </div>
    </div>
  );
}
